import { useCallback } from 'react';
import { pick, prop } from 'ramda';

import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { convertDate, getCurrentDateObj } from 'js/app/hooks/useSegmentation/helpers';
import { useGetServerTime } from 'js/app/hooks/useServerTime';
import { useIsAdmin } from 'js/hooks/user/hooks/useIsAdmin';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import {
    DAILY,
    MONTHLY,
    RANGE,
    RULE_MODIFIER,
    RULE_MODIFIER_MAP,
    TAG_TYPE_SEGMENTATION,
    USER_VISIBILITY,
    WEEKLY,
    YEARLY } from './constants';

const checkRangeDate = ({ fromDate, fromTime, toDate, toTime, currentServerTime }) => {
    const fromDateInMS = convertDate(fromDate, fromTime);
    const toDateInMS = convertDate(toDate, toTime);

    return currentServerTime >= fromDateInMS && currentServerTime <= toDateInMS;
};

const checkPeriod = (scheduleData, currentServerTime) => {
    switch (scheduleData.period) {
        case RANGE: {
            return checkRangeDate({ ...scheduleData, currentServerTime });
        }

        case DAILY: {
            const { fullDate } = getCurrentDateObj(currentServerTime);
            const { fromTime, toTime } = scheduleData;

            return checkRangeDate({
                currentServerTime,
                fromTime,
                toTime,
                fromDate: fullDate,
                toDate: fullDate
            });
        }

        case WEEKLY: {
            const { weekDayLong } = getCurrentDateObj(currentServerTime);
            const { days } = scheduleData;

            return days.includes(weekDayLong);
        }

        case MONTHLY: {
            const { dd } = getCurrentDateObj(currentServerTime);
            const { months } = scheduleData;

            return months.map(Number).includes(Number(dd));
        }

        case YEARLY: {
            const { dd, mm } = getCurrentDateObj(currentServerTime);
            const { dateOfYear } = scheduleData;

            return dateOfYear === `${dd}-${mm}`;
        }

        default:
            return true;
    }
};

export const useFilterByDate = () => {
    const [dTagTimezoneOffsetParameter] = useDynamicTags([DYNAMIC_TAG_NAMES.timezoneOffsetParameter]);
    const getServerTime = useGetServerTime(dTagTimezoneOffsetParameter);

    return useCallback((scheduleProp) => {
        if (!scheduleProp) {
            return true;
        }

        const schedule = Array.isArray(scheduleProp) ? scheduleProp : [scheduleProp];

        const currentServerTime = getServerTime();
        const { ranges, periods } = schedule.reduce((acc, item) => {
            if (item.period === 'range') {
                acc.ranges.push(item);
            } else {
                acc.periods.push(item);
            }

            return acc;
        }, { ranges: [], periods: [] });

        return periods.every(item => checkPeriod(item, currentServerTime))
            && (ranges.length ? ranges.some(item => checkPeriod(item, currentServerTime)) : true);
    }, [getServerTime]);
};

export const useFilterByUserSegmentation = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const totalDepositCount = parseInt(useUserSelector((state) => state.paymentsStatistics.deposits?.totalDepositCount || '0'));

    return useCallback(visibility => {
        if (!visibility) {
            return true;
        }

        const getPaymentCount = (count) => {
            if (!isLoggedIn || !totalDepositCount) {
                return false;
            }

            switch (count) {
                case 0:
                    return totalDepositCount === 0;
                case 1:
                    return totalDepositCount === 1;
                default:
                    return totalDepositCount >= 2;
            }

        };

        switch (visibility) {
            case USER_VISIBILITY.all:
                return true;
            case USER_VISIBILITY.guest:
                return !isLoggedIn;
            case USER_VISIBILITY.user:
                return isLoggedIn;
            case USER_VISIBILITY.noDeposit:
                return getPaymentCount(0);
            case USER_VISIBILITY.depositOneTime:
                return getPaymentCount(1);
            case USER_VISIBILITY.depositTwoOrMore:
                return getPaymentCount(2);
            default:
                return true;
        }
    }, [isLoggedIn, totalDepositCount]);
};

export const isProfileListed = (tagSegmentation, urlTemplates) => urlTemplates
    .some((userTemplate) => {
        const configuredTags = tagSegmentation.split(',');

        return configuredTags.some((configuredTag) => configuredTag.trim() === userTemplate.trim());
    });

export const useFilterTagSegmentation = () => {
    const { isLoggedIn, urlTemplates } = useUserSelector(pick(['isLoggedIn', 'urlTemplates']));

    return useCallback(tagSegmentation => {
        if (!isLoggedIn || !tagSegmentation) {
            return true;
        }

        if (!urlTemplates?.length) {
            return false;
        }

        return isProfileListed(tagSegmentation, urlTemplates);
    }, [isLoggedIn, urlTemplates]);
};

export const useFilterByAgeVerificationStatus = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const ageVerificationStatus = useUserSelector((state) => state.userDetails.ageVerificationStatus);

    return useCallback((avStatus) => {
        return !isLoggedIn || !avStatus || avStatus === 'all' || avStatus === ageVerificationStatus;
    }, [isLoggedIn, ageVerificationStatus]);
};

export const useFilterByBusinessPhase = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const accountBusinessPhase = useUserSelector((state) => state.userDetails.accountBusinessPhase);

    return useCallback((phase) => {
        return !isLoggedIn || !phase || phase === 'all' || phase === accountBusinessPhase;
    }, [isLoggedIn, accountBusinessPhase]);
};

export const useCheckRule = () => {
    const filterByDate = useFilterByDate();
    const filterTagSegmentation = useFilterTagSegmentation();
    const filterByBusinessPhase = useFilterByBusinessPhase();
    const filterByUserSegmentation = useFilterByUserSegmentation();
    const filterByAgeVerificationStatus = useFilterByAgeVerificationStatus();

    return useCallback(({
        modifier = RULE_MODIFIER.and,
        schedule,
        avStatus,
        visibility,
        businessPhase,
        segmentationTag
    }) => {
        const check = filterByDate(schedule)
            && filterByUserSegmentation(visibility)
            && filterByBusinessPhase(businessPhase)
            && filterTagSegmentation(segmentationTag)
            && filterByAgeVerificationStatus(avStatus);

        return {
            modifier: RULE_MODIFIER_MAP[modifier],
            check: [RULE_MODIFIER.orNot, RULE_MODIFIER.andNot].includes(modifier) ? !check : check
        };
    }, [filterByDate, filterByUserSegmentation, filterTagSegmentation, filterByBusinessPhase, filterByAgeVerificationStatus]);
};

export const useSegmentation = () => {
    const isAdmin = useIsAdmin();
    const checkRule = useCheckRule();
    const tags = useAppSelector(prop('tags'));

    return useCallback(segmentation => {
        if (!segmentation || isAdmin) {
            return true;
        }

        const { additionalRules = [], tagName, ...restRule } = segmentation;
        let segmentationOptions = [restRule, ...additionalRules];

        if (tagName) {
            segmentationOptions = segmentationOptions.concat(
                tags
                    ?.find(({ name, tagTypeId }) => name.trim() === tagName.trim() && tagTypeId === TAG_TYPE_SEGMENTATION)
                    ?.config?.segmentation || []
            );
        }

        const { orRules, andRules } = segmentationOptions
            .reduce((acc, rule) => {
                const { modifier, check } = checkRule(rule);

                if (modifier === RULE_MODIFIER.or) {
                    acc.orRules.push(check);
                } else {
                    acc.andRules.push(check);
                }

                return acc;
            }, { orRules: [], andRules: [] });

        return orRules.some(Boolean) || andRules.every(Boolean);
    }, [checkRule, isAdmin, tags]);
};

export default useSegmentation;
